import * as React from 'react';
import {
    List,
    EditButton,
    Datagrid,
    TextField,
    useRecordContext,
    TopToolbar,
    useListContext,
    Link,
    useNotify,
    ListContextProvider,
    Pagination,
} from 'react-admin';
import FormattedDateTimeField from '../../common/FormattedDateTimeField';
import { Button, Typography, Switch } from '@mui/material';
import { Add } from '@mui/icons-material';
import useIsPermission from '../../hooks/useIsPermission';
import { goApi } from '../../services';
import useLiveChannelList from '../../hooks/useLiveChannel';
import LiveClassGuideModal from '../live_videos/LiveClassGuideModal';

export const LiveVideoStatus = () => {
    const record = useRecordContext();

    return (
        <>
            {record.status === 'disabled' ? (
                <div className="text-xs px-1 py-0.5 rounded-md bg-red-500">Disabled</div>
            ) : record.status === 'idle' ? (
                <div className="text-xs px-1 py-0.5 rounded-md bg-green-500 text-white">idle</div>
            ) : (
                <></>
            )}
        </>
    );
};

const EnableDisableButton = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const { refetch } = useListContext();
    const [loading, setLoading] = React.useState(false);

    const handleToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();
        setLoading(true);

        const newStatus = event.target.checked ? 'enable' : 'disable';
        try {
            await goApi.get(`/live-videos/channel/${record.referId}/${newStatus}`);
            notify(`Channel ${newStatus}d successfully`, { type: 'success' });
            refetch();
        } catch (error) {
            notify(`Failed to ${newStatus} channel`, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Switch
            checked={record.status !== 'disabled'}
            onChange={handleToggle}
            disabled={loading}
            color="primary"
            onClick={e => e.stopPropagation()}
        />
    );
};
const ListActions = () => {
    const { isWritable } = useIsPermission('VIDEO');
    const { filterValues } = useListContext();

    const query = new URLSearchParams(filterValues).toString();

    return (
        <TopToolbar>
            {isWritable && (
                <>
                    <LiveClassGuideModal />
                    <Link to={`/live_channels/create?${query}`} style={{ textDecoration: 'none' }}>
                        <Button
                            variant="text"
                            startIcon={<Add />}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                color: 'rgba(0, 0, 0, 0.87)',
                                border: 'none',
                                backgroundColor: 'transparent',
                                padding: '8px 16px',
                                fontSize: '14px',
                            }}
                        >
                            <Typography variant="button">Create</Typography>
                        </Button>
                    </Link>
                </>
            )}
        </TopToolbar>
    );
};

const LiveChannelList = () => {
    const { channels, loading, error, refetch } = useLiveChannelList();
    const [page, setPage] = React.useState(1);
    const [perPage, setPerPage] = React.useState(10);
    const formattedChannels = channels.map((channel: any) => ({
        ...channel,
        id: channel.id || channel.referId,
    }));

    const startIndex = (page - 1) * perPage;
    const endIndex = startIndex + perPage;
    const paginatedData = formattedChannels.slice(startIndex, endIndex);

    const listContext = {
        data: paginatedData,
        isLoading: loading,
        isFetching: loading,
        total: formattedChannels.length,
        page: page,
        perPage: perPage,
        setPage: setPage,
        setPerPage: (newPerPage: number) => {
            setPerPage(newPerPage);
            setPage(1);
        },
        setSort: () => {},
        filterValues: {},
        setFilters: () => {},
        displayedFilters: {},
        showFilter: () => {},
        hideFilter: () => {},
        resource: 'live_channels',
        sort: { field: 'createdAt', order: 'DESC' as const },
        selectedIds: [],
        onSelect: () => {},
        onToggleItem: () => {},
        onUnselectItems: () => {},
        refetch: refetch,
        hasNextPage: endIndex < formattedChannels.length,
        hasPreviousPage: page > 1,
    };

    return (
        <div className="">
            <ListContextProvider value={listContext}>
                <TopToolbar>
                    <ListActions />
                </TopToolbar>
                <Datagrid bulkActionButtons={false}>
                    <TextField source="name" label="Name" />
                    <TextField source="streamKey" label="Stream Key" />
                    <TextField source="playbackKey" label="Playback Key" />
                    <LiveVideoStatus />
                    <FormattedDateTimeField source="createdAt" label="Created At" />
                    <EditButton />
                    <EnableDisableButton />
                </Datagrid>
                <Pagination />
            </ListContextProvider>
        </div>
    );
};
export default LiveChannelList;
